import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CalculationData {
  category: string;
  type: string;
  value: number;
  energyLabel: string;
  biobased: boolean;
  housingType: string;
  buildYear: string;
  postcode: string;
  executionDate: string;
}

interface SubsidyResult {
  totalSubsidy: number;
  breakdown: {
    baseAmount: number;
    unitAmount: number;
    bonusAmount: number;
    biobased: number;
  };
  warnings: string[];
  measure: any;
}

export const useISDECalculation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [subsidyResult, setSubsidyResult] = useState<SubsidyResult | null>(null);

  const calculateSubsidy = async (data: CalculationData) => {
    setIsLoading(true);
    
    try {
      // Fetch the measure from database
      const { data: measures, error } = await supabase
        .from('isde_measures')
        .select('*')
        .eq('category', data.category)
        .eq('type', data.type)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      const measure = measures;
      const warnings: string[] = [];
      
      // Check if subsidizable
      if (measure.base_amount === 0 && measure.per_unit_amount === 0) {
        setSubsidyResult({
          totalSubsidy: 0,
          breakdown: { baseAmount: 0, unitAmount: 0, bonusAmount: 0, biobased: 0 },
          warnings: [`${data.type} komt niet in aanmerking voor ISDE subsidie.`],
          measure
        });
        return;
      }

      // Validate minimum/maximum values
      if (measure.min_value && data.value < measure.min_value) {
        warnings.push(`Minimum vereist: ${measure.min_value} ${measure.unit}`);
      }
      
      if (measure.max_value && data.value > measure.max_value) {
        warnings.push(`Maximum toegestaan: ${measure.max_value} ${measure.unit} per aanvraag`);
      }

      // Calculate subsidy components
      let baseAmount = measure.base_amount || 0;
      let unitAmount = 0;
      let bonusAmount = 0;
      let biobasedAmount = 0;

      // Calculate per-unit amount
      if (measure.per_unit_amount > 0 && data.value > 0) {
        if (data.category === 'warmtepomp') {
          // For heat pumps: base + (kW - 1) * rate
          unitAmount = Math.max(0, data.value - 1) * measure.per_unit_amount;
        } else {
          // For insulation: value * rate
          unitAmount = data.value * measure.per_unit_amount;
        }
      }

      // Calculate bonus amounts
      if (measure.bonus_label === 'A+++' && data.energyLabel === 'A+++') {
        bonusAmount = measure.bonus_amount || 0;
      }

      if (measure.bonus_label === 'biobased' && data.biobased) {
        biobasedAmount = data.value * (measure.bonus_amount || 0);
      }

      // Add specific warnings
      if (data.category === 'isolatie' && data.value < 3) {
        warnings.push('Minimum oppervlakte voor isolatie is 3 m² (2025 regels)');
      }

      if (data.executionDate && data.executionDate.includes('2024')) {
        warnings.push('Voor maatregelen uit 2024 gelden mogelijk andere tarieven. Bewaar uw aankoopbewijs uit 2024.');
      }

      // Calculate total
      const totalSubsidy = baseAmount + unitAmount + bonusAmount + biobasedAmount;

      setSubsidyResult({
        totalSubsidy,
        breakdown: {
          baseAmount,
          unitAmount,
          bonusAmount,
          biobased: biobasedAmount
        },
        warnings,
        measure
      });

    } catch (error) {
      console.error('Error calculating subsidy:', error);
      setSubsidyResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    calculateSubsidy,
    isLoading,
    subsidyResult
  };
};